import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, Loader2, Flame, Star } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TemplateCreateModal from "@/components/TemplateCreateModal";
import LoopVideo from "@/components/LoopVideo";

export default function TemplateDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get(`/templates/${id}`).then(({ data }) => setT(data)).catch(() => setNotFound(true));
  }, [id]);

  const create = () => { if (!user) { navigate(`/login?redirect=/t/${id}`); return; } setOpen(true); };

  if (notFound) return (
    <div className="min-h-screen bg-ink-950 text-[#F0E9DD] flex flex-col items-center justify-center gap-4">
      <p className="text-zinc-400">Template not found.</p>
      <button onClick={() => navigate("/home")} className="text-gold">← Back home</button>
    </div>
  );
  if (!t) return <div className="min-h-screen bg-ink-950 flex items-center justify-center"><Loader2 className="animate-spin text-gold" /></div>;

  const bypass = user?.role === "admin" && user?.bypass_credits;

  return (
    <div className="min-h-screen bg-ink-950 text-[#F0E9DD]" data-testid="template-details">
      {/* Large preview */}
      <div className="relative w-full h-[55vh] sm:h-[62vh] overflow-hidden">
        {t.preview_video
          ? <LoopVideo src={t.preview_video} autoplay className="w-full h-full object-cover" />
          : <img src={t.thumbnail} alt={t.title} className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/30 to-ink-950/40" />
        <button onClick={() => navigate(-1)} data-testid="template-back" className="absolute top-4 left-4 w-10 h-10 rounded-full bg-ink-950/70 backdrop-blur flex items-center justify-center hover:bg-ink-950 transition-colors"><ArrowLeft size={20} /></button>
        <div className="absolute top-4 right-4 flex gap-2">
          {t.trending && <span className="bg-gold text-ink-950 text-[10px] font-bold rounded-full px-2.5 py-1 flex items-center gap-1"><Flame size={11} /> Trending</span>}
          {t.featured && <span className="bg-[#F0E9DD] text-ink-950 text-[10px] font-bold rounded-full px-2.5 py-1 flex items-center gap-1"><Star size={11} /> Featured</span>}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 -mt-16 relative z-10 pb-40">
        <div className="label-mono mb-2">{t.category} · {t.output_type === "video" ? "Video" : "Image"}</div>
        <h1 className="font-heading font-black uppercase tracking-tight text-3xl sm:text-5xl">{t.title}</h1>
        <p className="mt-4 text-[#A89F94] leading-relaxed">{t.description || "Upload your photo and generate with one tap."}</p>

        {t.example_outputs?.length > 0 && (
          <div className="mt-8">
            <div className="label-mono mb-3">Example outputs</div>
            <div className="grid grid-cols-3 gap-3">
              {t.example_outputs.map((u, i) => (
                <div key={i} className="aspect-[3/4] rounded-xl overflow-hidden border border-white/10">
                  <img src={u} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 flex items-center gap-3 text-sm text-[#A89F94]">
          <Sparkles size={16} className="text-gold" />
          Estimated credits: <span className="text-gold font-mono font-semibold" data-testid="template-cost">{bypass ? "Free (admin)" : t.credit_cost}</span>
        </div>
      </div>

      {/* Fixed Create button */}
      <div className="fixed bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-ink-950 via-ink-950 to-transparent p-4 sm:p-6">
        <div className="max-w-3xl mx-auto">
          <button onClick={create} data-testid="template-create-cta"
            className="w-full flex items-center justify-center gap-2 bg-gold text-ink-950 font-heading font-bold text-lg py-4 rounded-xl hover:bg-goldlight transition-colors">
            <Sparkles size={20} /> {t.output_type === "video" ? "Create Video" : "Create"}
          </button>
        </div>
      </div>

      <TemplateCreateModal template={t} open={open} onOpenChange={setOpen} />
    </div>
  );
}
